import "./index.css";
import React, { useCallback, useEffect, useMemo } from "react";
import ShopType from "@/enum/ShopType";
import ShopDTO from "@/dto/shop/ShopDTO";
import { Modal } from "antd";
import { useHistory } from "react-router";
import { isEcMarketShopExpired } from "@/utils/ShopUtils";
import { observer } from "mobx-react-lite";
import { useGlobalStore } from "@/store/hooks";
import { useModalStore } from "@/hooks/Modal/hooks";
import SyncOrderProcessDialog from "../dialogs/SyncOrderProcessDialog";
import { PremiumFunctionType, usePremiumValidator } from "@/services/PremiumValidator";
import DropdownOrderSyncButton from "./DropdownOrderSyncButton";
import { useOrderManagementStore } from "../../store/OrderManagementStore";

interface OrderSyncButtonProps {
    shop: ShopDTO;
}

const OrderSyncButton: React.FC<OrderSyncButtonProps> = ({ shop }) => {
    const globalStore = useGlobalStore();
    const { validate, loading } = usePremiumValidator();
    const shopSetting = globalStore.shopSettingMapping.get(shop.id);
    const history = useHistory();
    const orderManagementStore = useOrderManagementStore();
    const { fromAdmin } = useGlobalStore();
    const modalStore = useModalStore();

    const syncIncrementally = useCallback(async () => {
        const expired = isEcMarketShopExpired({
            type: shop.type,
            subscriptionEndTime: shopSetting?.subscriptionEndTime,
        });

        if (expired) {
            Modal.error({
                title: `该店铺(${shop.name})已过期，请先续费`,
                okCancel: true,
                okText: "去续费",
                onOk() {
                    history.push(`/setting/shop/appSubscription/index?shopId=${shop.id}`);
                },
            });

            return;
        }

        // 如果是拼多多代发店铺 需要限制自动同步
        if (shopSetting?.type === ShopType.PDD_FDS) {
            const shouldNext = await validate({
                type: PremiumFunctionType.PDD_FDS_SYNC_ORDER,
                shopId: shopSetting.id,
            });

            if (!shouldNext) {
                return;
            }
        } else if (shopSetting?.type === ShopType.PDD_FULFILLMENT) {
            const shouldNext = await validate({
                type: PremiumFunctionType.PDD_FULFILLMENT_SYNC_ORDER,
                shopId: shopSetting.id,
            });

            if (!shouldNext) {
                return;
            }
        }

        modalStore.openModal(SyncOrderProcessDialog, {
            shopId: shop.id,
            orderSyncType: "INCREMENT",
        });
    }, [shop, shopSetting]);

    const lastSyncOrderTime = useMemo(() => {
        if (shop.type !== ShopType.MANUAL) {
            let _lastSyncOrderTime = shopSetting?.lastSyncOrderTime;

            if (_lastSyncOrderTime) {
                _lastSyncOrderTime = shopSetting?.lastSyncOrderTime.substring(5, 16); // 移除年份和秒
            }

            return (
                <div className="inline-block text-muted text-small mr10" style={{ verticalAlign: "middle" }}>
                    <p>最后同步时间</p>
                    <p>{_lastSyncOrderTime}</p>
                </div>
            );
        }

        return null;
    }, [shop, shopSetting?.lastSyncOrderTime]);

    useEffect(() => {
        if (shop?.id) {
            globalStore.getShopSetting([shop.id]);
        }
    }, [shop.id]);

    // 店铺变化时 如果用户开启了自动同步订单属性，则自动同步
    useEffect(() => {
        if (shopSetting && shopSetting.enableAutoSyncOrder && !fromAdmin) {
            syncIncrementally();
        }
    }, [shopSetting?.id]);

    useEffect(() => {
        orderManagementStore.orderSyncStore.resetSyncProgress();
    }, [shop.id]);

    return (
        <div className="order-sync-button-container">
            <div>
                {lastSyncOrderTime}

                <DropdownOrderSyncButton shop={shop} syncIncrementally={syncIncrementally} />
            </div>
        </div>
    );
};

export default observer(OrderSyncButton);
